import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle2,
  Sun,
  AlertTriangle,
  Building2,
  User,
  MessageSquare
} from "lucide-react";
import { submitContactInquiry } from "../Services/userServices";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusError, setStatusError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateContactForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setStatusError("Please enter a valid email address.");
      return false;
    }
    if (formData.phone.trim()) {
      const cleaned = formData.phone.replace(/[\s\-\+]/g, "");
      const last10 = cleaned.slice(-10);
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(last10)) {
        setStatusError("Please enter a valid 10-digit mobile number.");
        return false;
      }
    }
    if (formData.message.trim().length < 10) {
      setStatusError("Please enter a message with at least 10 characters.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusError("");
    if (!validateContactForm()) return;

    setIsSubmitting(true);
    const result = await submitContactInquiry(formData);

    if (result.success) {
      setIsSubmitted(true);
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setIsSubmitted(false), 8000);
    } else {
      setStatusError(result.error || "Something went wrong. Please try again later.");
    }
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Corporate Headquarters",
      details: "Sector 8, behind Central Avenue, Kamal Vihar, Raipur, Chhattisgarh 492015",
    },
    {
      icon: Phone,
      title: "Direct Support Line",
      details: "0 97557 94514",
      sub: "Mon–Sat, 11:00 AM – 6:00 PM",
    },
    {
      icon: Mail,
      title: "Electronic Mail",
      details: "info@devdeepamsolar.com",
      sub: "Priority 24-Hour Response",
    },
  ];

  return (
    <section className="relative min-h-screen overflow-x-hidden bg-[#050B14] pt-24 pb-12 sm:pt-32 sm:pb-16 px-4 sm:px-6 lg:px-8 font-sans">
      
      {/* 🌟 LUXURIOUS CYBER BACKGROUND 🌟 */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1920&q=90" 
          alt="Solar Panels"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/95 via-[#0D3F7F]/60 to-[#050B14]/95" />
      </div>

      {/* Cyber Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-0" style={{ backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

      {/* Ambient Glows */}
      <div className="absolute top-1/4 -right-20 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/15 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none z-0" />
      <div className="absolute bottom-1/4 -left-20 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#1E5CB3]/20 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none z-0" />

      <div className="relative max-w-7xl mx-auto z-10 flex flex-col justify-center min-h-[calc(100vh-140px)]">
        
        {/* 🌟 1. HEADER SECTION (Compact) 🌟 */}
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#0D3F7F]/40 border border-[#1E5CB3]/40 px-3.5 py-1.5 rounded-full shadow-sm backdrop-blur-md mb-3 sm:mb-4">
            <Sun className="w-3.5 h-3.5 text-[#F7941D]" />
            <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.15em] text-[#F0F7FF] uppercase">
              Global Support Network
            </span>
          </div>
          <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight leading-tight px-1">
            Let’s Engineer Your <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent drop-shadow-sm">
              Energy Independence
            </span>
          </h1>
        </motion.div>

        {/* 🌟 2. SPLIT LAYOUT GRID 🌟 */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 items-stretch">
          
          {/* ⚡ LEFT COLUMN: CONTACT CARDS & MAP ⚡ */}
          <motion.div
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 flex flex-col gap-4 sm:gap-5"
          >
            {contactInfo.map((item, idx) => (
              <div
                key={idx}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-5 shadow-lg hover:bg-white/10 hover:border-[#F7941D]/30 transition-all duration-300 group flex items-start gap-3.5 sm:gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-[#0D3F7F]/50 border border-[#1E5CB3]/30 flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:bg-[#F7941D] group-hover:border-[#F7941D] transition-all duration-300 shadow-sm">
                  <item.icon className="w-4 h-4 sm:w-4.5 sm:h-4.5 text-[#FFC107] group-hover:text-white shrink-0" />
                </div>
                <div className="min-w-0">
                  <h4 className="text-[12px] sm:text-[13px] font-bold text-white mb-1 tracking-wide group-hover:text-[#F7941D] transition-colors">{item.title}</h4>
                  <p className="text-[11px] sm:text-[12px] text-slate-300 font-medium leading-relaxed break-words">{item.details}</p>
                  {item.sub && <p className="text-[9px] sm:text-[10px] text-slate-500 font-bold mt-1 uppercase tracking-wider">{item.sub}</p>}
                </div>
              </div>
            ))}

            {/* Embedded Cyber Map */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-1.5 shadow-lg overflow-hidden h-40 sm:h-48 group hover:border-[#1E5CB3]/40 transition-colors">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d475457.56445803697!2d81.6753908!3d21.4091026!3m2!1i1024!2i768!4f13.1!2m1!1sdevdepam%20solar%20raipur!5e0!3m2!1sen!2sin!4v1788252136415!5m2!1sen!2sin"
                width="100%" height="100%"
                style={{ border: 0, borderRadius: "0.8rem", filter: "invert(90%) hue-rotate(180deg) brightness(80%) contrast(120%)" }}
                allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                className="opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                title="Office Location"
              />
            </div>
          </motion.div>

          {/* ⚡ RIGHT COLUMN: ADVANCED GLASS FORM ⚡ */}
          <motion.div
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-7 relative"
          >
            {/* Outer Glow Outline */}
            <div className="absolute -inset-0.5 rounded-[1.25rem] sm:rounded-[1.5rem] bg-gradient-to-r from-[#0D3F7F] via-[#F7941D] to-[#0D3F7F] opacity-15 blur-lg pointer-events-none" />

            <div className="bg-[#050B14]/80 backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-[1.5rem] p-4 xs:p-6 sm:p-8 lg:p-10 shadow-2xl relative z-10 h-full flex flex-col justify-center">
              
              <div className="mb-5 border-b border-white/10 pb-4">
                <h3 className="text-lg sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
                  Send Digital Query <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 text-[#F7941D] shrink-0" />
                </h3>
                <p className="text-[11px] sm:text-[12px] text-slate-400 font-medium mt-1">Our engineering desk will respond within 24 hours.</p>
              </div>

              {/* Status Message Alerts */}
              <AnimatePresence>
                {statusError && (
                  <motion.div
                    initial={{ opacity: 0, height: 0, marginBottom: 0 }} animate={{ opacity: 1, height: 'auto', marginBottom: 16 }} exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                    className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] sm:text-[11px] font-bold flex items-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span>{statusError}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              {isSubmitted ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                  className="flex flex-col items-center justify-center py-8 text-center"
                >
                  <div className="w-14 h-14 sm:w-16 h-16 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-7 h-7 sm:w-8 sm:h-8 text-[#10B981]" />
                  </div>
                  <h4 className="text-white text-xl sm:text-2xl font-black mb-1.5">Message Transmitted!</h4>
                  <p className="text-slate-400 text-xs sm:text-[13px] font-medium max-w-sm px-2">
                    Thank you for reaching out to Devdeepam Solar. Our engineers are reviewing your request.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 text-left flex-1 flex flex-col justify-between">
                  
                  {/* Row 1 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-[#F7941D] transition-colors" />
                      <input
                        type="text" name="name" value={formData.name} onChange={handleChange} required
                        placeholder="Full Name"
                        className="w-full bg-[#03060C] border border-white/10 rounded-xl pl-11 pr-4 py-3 sm:py-3.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] focus:ring-1 focus:ring-[#F7941D] text-xs sm:text-[13px] font-semibold transition-all"
                      />
                    </div>
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-[#F7941D] transition-colors" />
                      <input
                        type="email" name="email" value={formData.email} onChange={handleChange} required
                        placeholder="Email Address"
                        className="w-full bg-[#03060C] border border-white/10 rounded-xl pl-11 pr-4 py-3 sm:py-3.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] focus:ring-1 focus:ring-[#F7941D] text-xs sm:text-[13px] font-semibold transition-all"
                      />
                    </div>
                  </div>

                  {/* Row 2 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="relative group">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-[#F7941D] transition-colors" />
                      <input
                        type="tel" name="phone" maxLength={10} value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9+]/g, "") })}
                        placeholder="Mobile Number (Optional)"
                        className="w-full bg-[#03060C] border border-white/10 rounded-xl pl-11 pr-4 py-3 sm:py-3.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] focus:ring-1 focus:ring-[#F7941D] text-xs sm:text-[13px] font-semibold transition-all"
                      />
                    </div>
                    <div className="relative group">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-[#F7941D] transition-colors" />
                      <input
                        type="text" name="subject" value={formData.subject} onChange={handleChange}
                        placeholder="Subject Line"
                        className="w-full bg-[#03060C] border border-white/10 rounded-xl pl-11 pr-4 py-3 sm:py-3.5 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] focus:ring-1 focus:ring-[#F7941D] text-xs sm:text-[13px] font-semibold transition-all"
                      />
                    </div>
                  </div>

                  {/* Message Box */}
                  <div className="relative group flex-1">
                    <textarea
                      name="message" value={formData.message} onChange={handleChange} required rows="4"
                      placeholder="Describe your solar requirements or queries here..."
                      className="w-full h-full min-h-[100px] bg-[#03060C] border border-white/10 rounded-xl p-4 text-white placeholder-slate-600 focus:outline-none focus:border-[#F7941D] focus:ring-1 focus:ring-[#F7941D] text-xs sm:text-[13px] font-semibold transition-all resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit" disabled={isSubmitting}
                      className="w-full relative overflow-hidden rounded-xl p-[2px] font-bold transition-all duration-300 hover:scale-[1.01] active:scale-[0.98] disabled:opacity-50 group cursor-pointer touch-manipulation"
                    >
                      <span className="absolute inset-0 bg-gradient-to-r from-[#F7941D] via-[#FFC107] to-[#F7941D] opacity-70 group-hover:opacity-100 transition-opacity" />
                      <span className="relative flex items-center justify-center bg-gradient-to-r from-[#F7941D] to-[#E67A00] text-white text-xs sm:text-[13px] font-black py-3.5 sm:py-4 px-4 rounded-[10px] tracking-widest uppercase shadow-inner">
                        {isSubmitting ? (
                          <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span className="flex items-center gap-2">
                            Transmit Message <Send className="w-4 h-4 shrink-0" />
                          </span>
                        )}
                      </span>
                    </button>
                  </div>

                </form>
              )}
            </div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};

export default ContactUs;